<template>
  <v-row
    ref="measurementRow"
    class="measurement-rows"
    :class="{ 'full-rows': hasFullRows }"
    no-gutters
  >
    <PopoverMeasurement
      :reading="readingPm"
      :label="$t(`readings.${pmReadingType}`)"
      :unitsLabel="pmReadingUnitLabel"
      imageSrc="/img/measurements/pm.svg"
    />
    <PopoverMeasurement
      :reading="readingTemp"
      :label="$t(`readings.temp`)"
      :unitsLabel="tempReadingUnitLabel"
      imageSrc="/img/measurements/temp.svg"
      dataCy="temperatureMeasurement"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.Rh)"
      :isSupported="isMeasurementSupported(types.Rh)"
      :reading="readingHumidity"
      :label="$t(`readings.rh`)"
      :unitsLabel="$t('units.percent')"
      imageSrc="/img/measurements/rh.svg"
      dataCy="rhMeasurement"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.BaroInhg)"
      :isSupported="isMeasurementSupported(types.BaroInhg)"
      :reading="readingBp"
      :label="$t(`readings.baro_inhg`)"
      :unitsLabel="$t(`units.${pressureReadingUnitLabel}`)"
      imageSrc="/img/measurements/bp.svg"
      dataCy="pressureMeasurement"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.Co2)"
      :isSupported="isMeasurementSupported(types.Co2)"
      :reading="readingCo2"
      :label="$t(`readings.co2`)"
      :unitsLabel="$t('units.ppm')"
      imageSrc="/img/measurements/co2.svg"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.Tvoc)"
      :isSupported="isMeasurementSupported(types.Tvoc)"
      :reading="readingTvoc"
      :label="$t(`readings.tvoc`)"
      :unitsLabel="$t('units.mgm3')"
      imageSrc="/img/measurements/voc.svg"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.Ch2o)"
      :isSupported="isMeasurementSupported(types.Ch2o)"
      :reading="readingCh2o"
      :label="$t(`readings.ch2o`)"
      :unitsLabel="$t('units.ppb')"
      imageSrc="/img/measurements/ch2o.svg"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.Co)"
      :isSupported="isMeasurementSupported(types.Co)"
      :reading="readingCo"
      :label="$t(`readings.co`)"
      :unitsLabel="$t('units.ppm')"
      imageSrc="/img/measurements/co.svg"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.So2)"
      :isSupported="isMeasurementSupported(types.So2)"
      :reading="readingSo2"
      :label="$t(`readings.so2`)"
      :unitsLabel="$t('units.ppb')"
      imageSrc="/img/measurements/so2.svg"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.No2)"
      :isSupported="isMeasurementSupported(types.No2)"
      :reading="readingNo2"
      :label="$t(`readings.no2`)"
      :unitsLabel="$t('units.ppb')"
      imageSrc="/img/measurements/no2.svg"
    />
    <PopoverMeasurement
      v-if="isMeasurementVisible(types.O3)"
      :isSupported="isMeasurementSupported(types.O3)"
      :reading="readingO3"
      :label="$t(`readings.o3`)"
      :unitsLabel="$t('units.ppb')"
      imageSrc="/img/measurements/o3.svg"
    />
  </v-row>
</template>

<script lang="ts">
import { defineComponent, nextTick, type PropType } from 'vue'
import type { VRow } from 'vuetify/components'
import type { UnitOfMeasure } from '@fusion/units'
import PopoverMeasurement from '../popoverMeasurement.vue'
import { MeasurementType, measurementTypesKey } from '@/services/device-measurements'
import {
  getPMSelectedUnit,
  getTemperatureSelectedUnit,
  getPressureSelectedUnit
} from '@/services/user-settings'
import { getPMRawMeasurementType, getReadingUnitLabel } from '@/components/devicePopover/utils'
import {
  getReadingPM,
  getReadingTemperature,
  getReadingPressure,
  getReadingValue
} from '@/services/unit-conversion'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { MAP_GETTERS } from '@/store/map'
import type { GetterByDeviceId, GetterByModelSubmodel } from '@/components/devicePopover/types'
import type { FormattedMeasurement, SensorReadings } from '@/services/device-service/devices'

interface DeviceView {
  model: string
  submodel: string
  isMine: boolean
  isShared: boolean
}

export default defineComponent({
  name: 'measurementsSmall',
  props: {
    deviceId: { type: String, default: '' },
    permissions: { type: Array as PropType<string[]>, default: (): string[] => [] },
    sensorReadings: { type: Object as PropType<SensorReadings>, default: null }
  },
  data() {
    return {
      accountId: null as string | null,
      hasFullRows: false
    }
  },
  components: {
    PopoverMeasurement
  },
  computed: {
    deviceType(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_TYPE_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.deviceId)
    },
    types(): typeof MeasurementType {
      return MeasurementType
    },
    pmReadingType(): string {
      const measType = this.$store.getters[
        `${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`
      ] as number
      return getPMRawMeasurementType(measType)
    },
    pmReadingUnit(): UnitOfMeasure {
      return getPMSelectedUnit()
    },
    pmReadingUnitLabel(): string {
      return getReadingUnitLabel(this.pmReadingUnit)
    },
    pressureReadingUnitLabel(): string {
      return getReadingUnitLabel(this.pressureReadingUnit)
    },
    pressureReadingUnit(): UnitOfMeasure {
      return getPressureSelectedUnit()
    },
    tempReadingUnit(): UnitOfMeasure {
      return getTemperatureSelectedUnit()
    },
    tempReadingUnitLabel(): string {
      return getReadingUnitLabel(this.tempReadingUnit)
    },
    readingPm(): FormattedMeasurement | null {
      return getReadingPM(
        this.sensorReadings,
        this.pmReadingType,
        this.pmReadingUnit,
        this.deviceType
      )
    },
    readingTemp(): FormattedMeasurement | null {
      return getReadingTemperature(this.sensorReadings, this.tempReadingUnit, this.deviceType)
    },
    readingHumidity(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.Rh],
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.Rh)
      )
    },
    readingBp(): FormattedMeasurement | null {
      return getReadingPressure(
        this.sensorReadings,
        this.pressureReadingUnit,
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.BaroInhg)
      )
    },
    readingCo2(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.Co2],
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.Co2)
      )
    },
    readingCh2o(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.Ch2o],
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.Ch2o)
      )
    },
    readingTvoc(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.Tvoc],
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.Tvoc)
      )
    },
    readingCo(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.Co],
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.Co)
      )
    },
    readingSo2(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.So2],
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.So2)
      )
    },
    readingO3(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.O3],
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.O3)
      )
    },
    readingNo2(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.No2],
        this.deviceType,
        this.isMeasurementSupported(MeasurementType.No2)
      )
    },
    model(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.deviceId)
    },
    submodel(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.deviceId)
    },
    isMine(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      const deviceAccountId = getter(this.deviceId)
      return this.accountId === deviceAccountId
    },
    isShared(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
      ] as GetterByDeviceId<boolean>
      return getter(this.deviceId)
    },
    supportedMeasurements(): MeasurementType[] {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUPPORTED_MEASUREMENTS}`
      ] as GetterByModelSubmodel<MeasurementType[]>
      const isPublicOnly = !this.device.isMine && !this.device.isShared
      return getter(this.device.model, this.device.submodel, isPublicOnly)
    },
    visibleMeasurements(): MeasurementType[] {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUPPORTED_MEASUREMENTS}`
      ] as GetterByModelSubmodel<MeasurementType[]>
      return getter(this.device.model, this.device.submodel)
    },
    device(): DeviceView {
      return {
        model: this.model,
        submodel: this.submodel,
        isMine: this.isMine,
        isShared: this.isShared
      }
    }
  },
  watch: {
    async deviceId() {
      await this.setFullRows()
    }
  },
  methods: {
    isMeasurementVisible(measurementType: MeasurementType): boolean {
      return this.visibleMeasurements.includes(measurementType)
    },
    isMeasurementSupported(measurementType: MeasurementType): boolean {
      return this.supportedMeasurements.includes(measurementType)
    },
    setAccountId(): void {
      this.accountId = getUserAccountId(this.$auth)
    },
    async setFullRows(): Promise<void> {
      await nextTick()
      const measurementRowRef = this.$refs.measurementRow as VRow
      const row = measurementRowRef.$el as Element | null
      if (row) {
        this.hasFullRows = row.children.length % 3 === 0
      }
    }
  },
  async mounted() {
    this.setAccountId()
    await this.setFullRows()
  }
})
</script>

<style lang="scss" scoped>
.measurement-rows {
  padding-bottom: 12px;

  &.full-rows {
    padding-bottom: 56px;

    [class*='widget'] & {
      padding-bottom: 12px;
    }
  }
}
</style>
